import { useState } from "react";
import { EditIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

type SlaInlineEditorProps = {
  name: string;
  hours?: number;
  isPending: boolean;
  editLabel: string;
  onSave: (hours: number) => void;
};

const parseHours = (value: string) => {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const SlaInlineEditor = ({
  name,
  hours,
  isPending,
  editLabel,
  onSave,
}: SlaInlineEditorProps) => {
  const isEditing = useBoolean(false);
  const [value, setValue] = useState(hours === undefined ? "" : String(hours));

  const startEditing = () => {
    setValue(hours === undefined ? "" : String(hours));
    isEditing.setTrue();
  };

  if (!isEditing.value) {
    return (
      <div className="flex items-center gap-3" data-testid={`${name}-display`}>
        <span className="body-base-medium" data-testid={`${name}-value`}>
          {hours === undefined ? "—" : m.cases_settings_hoursSuffix({ hours })}
        </span>
        <ThemedButton
          ariaLabel={editLabel}
          dataTestId={`${name}-edit`}
          icon={<EditIcon />}
          onClick={startEditing}
          size="small"
          variant="tertiary"
        />
      </div>
    );
  }

  const parsedHours = parseHours(value);

  return (
    <div className="flex items-center gap-3">
      <InputControl
        name={name}
        onChange={(event) => setValue(event.target.value)}
        type="number"
        value={value}
        withBorder
      />
      <ThemedButton
        dataTestId={`${name}-cancel`}
        onClick={isEditing.setFalse}
        size="small"
        variant="tertiary"
      >
        {m.cases_modal_cancel()}
      </ThemedButton>
      <ThemedButton
        dataTestId={`${name}-save`}
        disabled={parsedHours === undefined || isPending}
        isLoading={isPending}
        onClick={() => {
          if (parsedHours !== undefined) {
            onSave(parsedHours);
            isEditing.setFalse();
          }
        }}
        size="small"
        variant="primary"
      >
        {m.cases_settings_save()}
      </ThemedButton>
    </div>
  );
};
